import { ChangeDetectionStrategy, Component, HostListener, OnInit } from '@angular/core';
import { AccountAttributesEnum, AddonsIdentifiersEnum, FeatureFlagsEnum, PredictiveIntentAutomationEnum, ResultTabsetUrlsEnum } from '../../enums';
import { ModalNavigationEnum } from '../../constants';
import { IntentSettingsHorizontalMenu, IntentSettingsVerticalTabParam } from '../../../modals/intent-settings/constants';
import { VerticalMenuTabsEnum } from '../../../modals/intent-settings/enums';
import { CsvSettingsModalComponent } from '../../../settings/components/csv-settings-modal/csv-settings-modal.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { AboutInfoModalComponent } from '../../../settings/components/about-info-modal/about-info-modal.component';
import { CreditBalanceModalComponent } from '../../../settings/components/credit-balance-modal/credit-balance-modal.component';
import { BuyerPersonaSettingsModalComponent } from '../../../settings/components/buyer-persona-settings-modal';
import { AuthenticationService, UserService } from '../../../core/services';
import { SI_ADSINTEL_URL } from '../../../core/config';
import { filter, map, startWith, switchMap, take } from 'rxjs/operators';
import { ServiceCreditItem, ServiceCreditTypesEnum, User } from '../../../core/models/user.model';
import { CommonUtilService } from '../../services/CommonUtil.service';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { AuthcodeGenerateCodeResponseDto } from '../../dto';
import { AuthcodeApiService } from '../../services/api';
import { LoadingService } from '../../services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { environment } from '../../../../environments/environment';
import { NavigationEnd, Router } from '@angular/router';

const ADSINTEL_LOADING_TAG: string = 'adsintel-results-loading-tag';
const PROSPECTCONNECT_LOADING_TAG: string = 'prospectconnect-results-loading-tag';

@UntilDestroy()
@Component({
    selector: 'app-new-sidenav',
    templateUrl: './new-sidenav.component.html',
    styleUrls: ['./new-sidenav.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class NewSidenavComponent implements OnInit {
  @HostListener('mouseenter') onMouseEnter(): void {
    clearTimeout(this.timeoutId);
    this.timeoutId = setTimeout(() => {
      this.isShownSubject.next(true);
    }, 300);
  }
  @HostListener('mouseleave') onMouseLeave(): void {
    clearTimeout(this.timeoutId);
    this.timeoutId = setTimeout(() => {
      this.isShownSubject.next(false);
    }, 300);
  }
  @HostListener('click', ['$event']) onClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    if (target.closest('app-nav-item')) {
      this.isShownSubject.next(false);
    }
  }

  isAdsIntelDisabled$: Observable<boolean> = this.loadingService.getIsLoading(ADSINTEL_LOADING_TAG);
  isProspectConnectDisabled$: Observable<boolean> = this.loadingService.getIsLoading(PROSPECTCONNECT_LOADING_TAG);

  url$: Observable<string>;
  isShown$: Observable<boolean>;

  readonly user$: Observable<User> = this.userService.getUserData();

  readonly resultTabsetUrlsEnum: typeof ResultTabsetUrlsEnum = ResultTabsetUrlsEnum;
  readonly modalNavigationEnum: typeof ModalNavigationEnum = ModalNavigationEnum;
  readonly accountAttributesEnum: typeof AccountAttributesEnum = AccountAttributesEnum;
  readonly serviceCreditTypesEnum: typeof ServiceCreditTypesEnum = ServiceCreditTypesEnum;
  readonly featureFlagsEnum: typeof FeatureFlagsEnum = FeatureFlagsEnum;
  readonly addonsIdentifiersEnum: typeof AddonsIdentifiersEnum = AddonsIdentifiersEnum;

  readonly intentAgentsQueryParams = {
    [IntentSettingsVerticalTabParam]: VerticalMenuTabsEnum.PREDICTIVEINTENT_AUTOMATIONS,
    [IntentSettingsHorizontalMenu]: PredictiveIntentAutomationEnum.EXISTING_AUTOMATIONS,
  };

  private timeoutId?: ReturnType<typeof setTimeout>;
  private readonly isShownSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    private readonly matDialog: MatDialog,
    private readonly userService: UserService,
    private readonly commonUtilService: CommonUtilService,
    private readonly authenticationService: AuthenticationService,
    private readonly authcodeApiService: AuthcodeApiService,
    private readonly loadingService: LoadingService,
    private readonly router: Router,
  ) {
  }

  ngOnInit(): void {
    this.url$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event) => event.urlAfterRedirects),
      startWith(this.router.url),
    );
    this.isShown$ = this.isShownSubject.asObservable();
  }

  onCSVSettings(): void {
    this.matDialog.open(CsvSettingsModalComponent, { panelClass: 'modal-md' });
  }

  onAboutSettings(): void {
    this.matDialog.open(AboutInfoModalComponent, { panelClass: 'modal-lg' });
  }

  onBuyerPersonaSettings(): void {
    this.matDialog.open(BuyerPersonaSettingsModalComponent, {
      panelClass: ['modal-lg', 'modal-dialog--fixed-height'],
    });
  }

  onCreditsSettings(): void {
    this.userService.getUserData().pipe(filter(Boolean), take(1)).subscribe((userData: User) => {
      const credits: ServiceCreditItem[] = [];
      userData?.service_credits?.forEach(credit => {
        if (credit.ad_hoc_credits > 0 || credit.license_credit_limit > 0 || credit.total_account_credit_limit > 0) {
          credits.push(credit);
        }
      });
      const config: MatDialogConfig = {
        panelClass: credits.length > 0 ? credits.length > 2 ? 'modal-xl' : credits.length === 1 ? 'modal-md' : 'modal-lg' : 'modal-sm',
      };
      this.matDialog.open(CreditBalanceModalComponent, config);
      this.commonUtilService.churnZeroEvents('Portal - Credit Balance - View');
    });
  }

  onLogout(): void {
    this.authenticationService.navigateToLogin();
  }

  onOpenAdsIntel(): void {
    this.userService.getUserData().pipe(filter(Boolean), take(1)).subscribe((user: User) => {
      if (!user.ads_intel_enabled) {
        this.fakeRequest(ADSINTEL_LOADING_TAG).pipe(take(1)).subscribe(() => {
          window.open(SI_ADSINTEL_URL, '_blank');
        });

        return;
      }

      this.generateCode(ADSINTEL_LOADING_TAG).pipe(untilDestroyed(this)).subscribe(({ code }: AuthcodeGenerateCodeResponseDto) => {
        window.open(`${environment.adsIntelUrl}?code=${code}`, '_blank');
      });
    });
  }

  onOpenProspectConnect(): void {
    this.generateCode(PROSPECTCONNECT_LOADING_TAG).pipe(untilDestroyed(this)).subscribe(({ code }: AuthcodeGenerateCodeResponseDto) => {
      window.open(`${environment.prospectConnectUrl}?code=${code}`, '_blank');
    });
  }

  private fakeRequest(loadingTag: string): Observable<number> {
    return this.loadingService.loadingWrapper(timer(100), loadingTag);
  }

  private generateCode(loadingTag: string): Observable<AuthcodeGenerateCodeResponseDto> {
    return this.loadingService.loadingWrapper(this.userService.listenToUserChangesByKey<number>('id').pipe(
      take(1),
      switchMap((userId: number) => this.authcodeApiService.generateCode(userId)),
    ), loadingTag);
  }
}
